import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";

export type SummaryBarItem = {
  label: ReactNode;
  value: ReactNode;
};

export type SummaryBarProps = HTMLAttributes<HTMLDivElement> & {
  items?: SummaryBarItem[];
  totalLabel: ReactNode;
  totalValue: ReactNode;
  note?: ReactNode;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const SummaryBarRoot = styled("div", {
  label: "AgenticSummaryBar-root"
})({
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

const SummaryBarRow = styled("div", {
  label: "AgenticSummaryBar-row"
})({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  columnGap: "1.25rem",
  rowGap: ".5rem"
});

const SummaryBarItemWrap = styled("div", {
  label: "AgenticSummaryBar-item"
})({
  display: "flex",
  alignItems: "center",
  gap: ".375rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const SummaryBarItemValue = styled("span", {
  label: "AgenticSummaryBar-itemValue"
})({
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const SummaryBarTotal = styled("div", {
  label: "AgenticSummaryBar-total"
})({
  marginLeft: "auto",
  display: "flex",
  alignItems: "baseline",
  gap: ".5rem"
});

const SummaryBarTotalLabel = styled("span", {
  label: "AgenticSummaryBar-totalLabel"
})({
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const SummaryBarTotalValue = styled("span", {
  label: "AgenticSummaryBar-totalValue"
})({
  fontSize: "1rem",
  lineHeight: "1.5rem",
  fontWeight: 700,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const SummaryBarNote = styled("div", {
  label: "AgenticSummaryBar-note"
})({
  marginTop: ".25rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

export const SummaryBar = forwardRef<HTMLDivElement, SummaryBarProps>(({ className, items = [], totalLabel, totalValue, note, ...props }, ref) => (
  <SummaryBarRoot ref={ref} className={className} {...props}>
    <SummaryBarRow>
      {items.map((item, index) => (
        <SummaryBarItemWrap key={index}>
          <span>{item.label}</span>
          <SummaryBarItemValue>{item.value}</SummaryBarItemValue>
        </SummaryBarItemWrap>
      ))}
      <SummaryBarTotal>
        <SummaryBarTotalLabel>{totalLabel}</SummaryBarTotalLabel>
        <SummaryBarTotalValue>{totalValue}</SummaryBarTotalValue>
      </SummaryBarTotal>
    </SummaryBarRow>
    {note && <SummaryBarNote>{note}</SummaryBarNote>}
  </SummaryBarRoot>
));

SummaryBar.displayName = "SummaryBar";
